import { useCallback, useMemo, useState } from 'react'
import type { InvoiceFormData, LineItem, PaymentMethod } from './types'

function createEmptyLineItem(): LineItem {
  return {
    id: Date.now().toString(),
    product_code: '',
    product_name: '',
    serial_number: '',
    quantity: 0,
    carat: 0,
    unit: '',
    weight: 0,
    stone_weight: 0,
    unit_price: 0,
    salary: 0,
    total_salary: 0,
    tax_rate: 0,
    discount: 0,
    total: 0
  }
}

function createEmptyPayment(): PaymentMethod {
  return {
    id: Date.now().toString(),
    method: '',
    amount: 0,
    currency: '',
    reference: '',
    currencyId: '',
    accountGuideId: ''
  }
}

export function createInitialFormData(): InvoiceFormData {
  return {
    invoice_number: '',
    invoice_date: '',
    def_branch: 0,
    staff_user_id: 0,
    branch: '',
    salesperson: '',
    payment_type: '',
    invoice_status: '',
    payment_method: '',
    document_number: '',
    customer_id: '',
    customer_code: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    identity_type: '',
    identity_number: '',
    tax_number: '',
    statement: '',
    seller: '',
    timing: '',
    currency: '',
    exchange_rate: 0,
    notes: '',
    line_items: [createEmptyLineItem()],
    payment_methods: [createEmptyPayment()],
    delivery_date: '',
    delivery_time: '',
    delivery_address: '',
    zatca_qr_code: '',
    zatca_uuid: '',
    zatca_hash: '',
    zatca_reported: false
  }
}

export function useInvoiceForm(initial?: InvoiceFormData) {
  const [formData, setFormData] = useState<InvoiceFormData>(initial || createInitialFormData())

  const recalcLineItem = (item: LineItem) => {
    const subtotal = item.quantity * item.unit_price
    const discountAmount = (subtotal * item.discount) / 100
    const taxable = subtotal - discountAmount
    const taxAmount = (taxable * item.tax_rate) / 100
    return { ...item, total: taxable + taxAmount }
  }

  const updateLineItem = useCallback((index: number, field: keyof LineItem, value: any) => {
    setFormData(prev => {
      const updatedItems = [...prev.line_items]
      const updatedItem = { ...updatedItems[index], [field]: value } as LineItem
      updatedItems[index] = recalcLineItem(updatedItem)
      return { ...prev, line_items: updatedItems }
    })
  }, [])

  const addLineItem = useCallback(() => {
    const newItem: LineItem = {
      id: Date.now().toString(),
      product_code: '',
      product_name: '',
      serial_number: '',
      quantity: 0,
      carat: 0,
      unit: '',
      weight: 0,
      stone_weight: 0,
      unit_price: 0,
      salary: 0,
      total_salary: 0,
      tax_rate: 0,
      discount: 0,
      total: 0
    }
    setFormData(prev => ({ ...prev, line_items: [...prev.line_items, newItem] }))
  }, [])

  const removeLineItem = useCallback((index: number) => {
    setFormData(prev => {
      if (prev.line_items.length <= 1) return prev
      return { ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }
    })
  }, [])

  const updatePaymentMethod = useCallback((index: number, field: keyof PaymentMethod, value: any) => {
    setFormData(prev => {
      const updated = [...prev.payment_methods]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, payment_methods: updated }
    })
  }, [])

  const addPaymentMethod = useCallback(() => {
    const newPayment: PaymentMethod = createEmptyPayment()
    setFormData(prev => ({ ...prev, payment_methods: [...prev.payment_methods, newPayment] }))
  }, [])

  const removePaymentMethod = useCallback((index: number) => {
    setFormData(prev => {
      if (prev.payment_methods.length <= 1) return prev
      return { ...prev, payment_methods: prev.payment_methods.filter((_, i) => i !== index) }
    })
  }, [])

  const applyDefaults = useCallback((defaults: Partial<InvoiceFormData>) => {
    setFormData(prev => ({ ...prev, ...defaults }))
  }, [])

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData())
  }, [])

  const grandTotal = useMemo(
    () => formData.line_items.reduce((sum, item) => sum + item.total, 0),
    [formData.line_items]
  )

  const totalPayments = useMemo(
    () => formData.payment_methods.reduce((sum, p) => sum + p.amount, 0),
    [formData.payment_methods]
  )

  return {
    formData,
    setFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    updatePaymentMethod,
    addPaymentMethod,
    removePaymentMethod,
    applyDefaults,
    resetForm,
    grandTotal,
    totalPayments
  }
}
